import { useId } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { cn } from '@bill-alps/ui';
import {
  toggleSidebarGroup,
  useCollapsedSidebarGroups,
} from '@/hooks/use-sidebar-groups';
import { SidebarNavItem } from './sidebar-nav-item';

interface SidebarNavGroupProps {
  /** Persisted identity of the group — the i18n key, stable across locales. */
  groupKey: string;
  label: string;
  items: { to: string; icon: LucideIcon; label: string }[];
  isCollapsed: boolean;
  onNavigate?: () => void;
}

export function SidebarNavGroup({
  groupKey,
  label,
  items,
  isCollapsed,
  onNavigate,
}: SidebarNavGroupProps) {
  const collapsedGroups = useCollapsedSidebarGroups();
  const itemsId = useId();

  // Rail mode has no labels, so it has nothing to click and nothing to fold:
  // every icon stays reachable behind a plain divider, as before.
  if (isCollapsed) {
    return (
      <div className="mt-4 flex flex-col gap-1">
        <div className="mx-2 my-2 border-t border-border" />
        {items.map((item) => (
          <SidebarNavItem
            key={item.to}
            to={item.to}
            icon={item.icon}
            label={item.label}
            isCollapsed
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  }

  const isOpen = !collapsedGroups.has(groupKey);

  return (
    <div className="mt-4 flex flex-col">
      <button
        type="button"
        onClick={() => toggleSidebarGroup(groupKey)}
        aria-expanded={isOpen}
        aria-controls={itemsId}
        className="mb-1.5 flex w-full cursor-pointer items-center justify-between rounded-lg px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
      >
        {label}
        <ChevronDown
          className={cn(
            'size-3.5 transition-transform duration-300 ease-spring',
            !isOpen && '-rotate-90',
          )}
        />
      </button>
      {/* Height animation in pure CSS: a grid row sliding between 0fr and 1fr
          reaches the content's natural height without measuring it in JS, so
          the items never unmount and no keyframe has to know how tall they are. */}
      <div
        id={itemsId}
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-spring',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        // Staying mounted is what buys the animation; `inert` is what keeps a
        // folded group out of the tab order and off the a11y tree meanwhile.
        inert={!isOpen}
      >
        <div className="flex flex-col gap-1 overflow-hidden">
          {items.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              isCollapsed={false}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
